import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, ReplaySubject, Subject, takeUntil } from 'rxjs';
import { Navigation } from 'app/core/navigation/navigation.types';
import { adminNavigation, orderOperatorNavigation } from './navigation.data';
import { cloneDeep } from 'lodash';
import { UserService } from '../user/user.service';
import { User } from '../user/user.types';
import { FuseNavigationItem } from '@fuse/components/navigation';
import { environment } from 'environments/environment';

interface PastEditionDescriptor {
    identifier: string;
    name: string;
    endDate: string | Date;
}

interface AdminSettingsResponse {
    pastEditions?: PastEditionDescriptor[];
}

@Injectable({
    providedIn: 'root'
})
export class NavigationService implements OnDestroy
{
    private _navigation: ReplaySubject<Navigation> = new ReplaySubject<Navigation>(1);
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    private _user: User;

    constructor(
        private _userService: UserService,
        private _httpClient: HttpClient
    ) {
        this._userService.user$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((user: User) => {
                this._user = user;
                this._buildNavigation();
            });
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    get navigation$(): Observable<Navigation>
    {
        return this._navigation.asObservable();
    }

    get(): Observable<Navigation>
    {
        return this._navigation.asObservable();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    ngOnDestroy(): void
    {
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------

    private _buildNavigation(): void
    {
        if (this._user?.role !== 'admin') {
            this._navigation.next({ default: cloneDeep(orderOperatorNavigation) });
            return;
        }

        // Emit the static admin nav immediately so the sidebar isn't blocked on the API call.
        this._navigation.next({ default: cloneDeep(adminNavigation) });

        // Then fetch settings and patch the `istoric-etape` node's children from PastEditions.
        this._httpClient.get<AdminSettingsResponse>(`${environment.httpBasePath}/settings/admin`)
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe({
                next: (settings) => {
                    const nav = cloneDeep(adminNavigation);
                    const archive = nav.find(n => n.id === 'istoric-etape');
                    if (archive) {
                        archive.children = this._buildHistoryChildren(settings?.pastEditions ?? []);
                    }
                    this._navigation.next({ default: nav });
                },
                error: () => { /* leave the empty placeholder in place on failure */ }
            });
    }

    private _buildHistoryChildren(pastEditions: PastEditionDescriptor[]): FuseNavigationItem[]
    {
        return [...pastEditions]
            .sort((a, b) => new Date(b.endDate).getTime() - new Date(a.endDate).getTime())
            .map((edition): FuseNavigationItem => ({
                id      : `history-${edition.identifier}`,
                title   : this._formatEditionTitle(edition),
                type    : 'collapsable',
                icon    : 'heroicons_outline:academic-cap',
                children: [
                    {
                        id   : `history-${edition.identifier}-sessions`,
                        title: 'Sesiuni',
                        type : 'basic',
                        icon : 'heroicons_outline:calculator',
                        link : `/history/${edition.identifier}/sessions`
                    },
                    {
                        id   : `history-${edition.identifier}-feedback`,
                        title: 'Feedback',
                        type : 'basic',
                        icon : 'heroicons_outline:color-swatch',
                        link : `/history/${edition.identifier}/feedback`
                    },
                    {
                        id   : `history-${edition.identifier}-tests`,
                        title: 'Teste',
                        type : 'basic',
                        icon : 'heroicons_outline:newspaper',
                        link : `/history/${edition.identifier}/tests`
                    }
                ]
            }));
    }

    private _formatEditionTitle(edition: PastEditionDescriptor): string
    {
        const name = edition.name ?? '';
        const year = new Date(edition.endDate).getFullYear();
        if (!year || Number.isNaN(year) || name.includes(String(year))) {
            return name;
        }
        return `${name} ${year}`.trim();
    }
}
